import { registerTours, type Tour } from '@flobi/asset-library/tours';
import { ALL_CHAPTERS } from './chapters';

/**
 * Artwork's tours, and which Get started step runs which (PLAN-2026-10-26-artwork-onboarding-tours.md).
 * The deck shows the server's steps (users-back onboarding.getSteps, app_slug "artwork"); each
 * step's `action_key` maps to a tour here. A key with no tour, or any key on a phone (no rings
 * there yet), keeps the card's plain link to its `frontend_route`.
 *
 * `completes`: the step a finished tour ticks (onboarding.completeByAction). `then`: the tour its
 * last card offers next, so the six read as one path.
 */
export const CHAPTERS = Object.fromEntries(ALL_CHAPTERS.map((c) => [c.id, c]));

/** [tour id, title, chapter, Get started step key] in order; each leads into the next. */
const ORDER: [string, string, string, string][] = [
  ['welcome', 'Welcome to Artwork', 'welcome', 'artwork_welcome'],
  ['first-design', 'Make your first design', 'first-design', 'artwork_first_design'],
  ['add', 'Add pictures and graphics', 'add', 'artwork_add'],
  ['ai', 'Make it with AI', 'ai', 'artwork_ai'],
  ['animate', 'Bring it to life', 'animate', 'artwork_animate'],
  ['share-export', 'Share and export', 'share-export', 'artwork_share_export'],
];

export const TOURS: Record<string, Tour> = Object.fromEntries(
  ORDER.map(([id, title, chapter, completes], i) => {
    const next = ORDER[i + 1];
    return [id, { id, title, chapters: [chapter], completes, ...(next ? { then: next[0] } : {}) }];
  }),
);

const TOUR_FOR_STEP: Record<string, string> = Object.fromEntries(ORDER.map(([id, , , key]) => [key, id]));

/** The tour a Get started step runs, or null (the card links to its frontend_route instead). */
export const tourForStep = (actionKey: string): string | null => TOUR_FOR_STEP[actionKey] ?? null;

registerTours({ tours: TOURS, chapters: CHAPTERS });

/**
 * Tips › Your path (the kit's TourPath): the Get started steps in sections, each with its badge.
 * Every tour is in one, in ORDER's order.
 */
export const PATH_SECTIONS = [
  { id: 'learn', title: 'Start here', steps: ['artwork_welcome', 'artwork_first_design'] },
  { id: 'basics', title: 'Make it yours', steps: ['artwork_add', 'artwork_ai'] },
  { id: 'further', title: 'Bring it out', steps: ['artwork_animate', 'artwork_share_export'] },
];

/** Where Artwork keeps a person's tour progress in their view prefs (the kit's progress.js). */
export const PROGRESS_KEY = 'artworkTours';
